import { memo, useCallback, useState } from 'react';

import { useRequest } from '@Contexts/Request';
import Input from '@UI/Input';
import InputPlaceholder from '@UI/InputPlaceholder';

import type { FC, ChangeEvent } from 'react';
import type { CountryBasedData } from '@Types/Base';
import type { InputProps } from '@UI/Input';
import type { InputProps as InputPlaceholderProps } from '@UI/InputPlaceholder';
interface DefaultInputPhoneProps extends InputProps {
  mode?: 'default';
}

interface RedesignInputPhoneProps extends InputPlaceholderProps {
  mode?: 'redesign';
}

export type InputPhoneProps = DefaultInputPhoneProps | RedesignInputPhoneProps;

const masks: CountryBasedData<string> = {
  RUS: '+9 (999) 999-99-99',
  BLR: '+375 (99) 999-99-99',
  KAZ: '+9 (999) 999-99-99',
  UZB: '+999 (99) 999-99-99',
};

const placeholders: CountryBasedData<string> = {
  RUS: '+7 (___) ___ __ __',
  BLR: '+375 (__) ___ __ __',
  KAZ: '+7 (___) ___ __ __',
  UZB: '+998 (__) ___ __ __',
};

const InputPhone: FC<InputPhoneProps> = (props) => {
  const { placeholder, value = '', mode, ...restProps } = props;
  const { country } = useRequest();
  const finalCountry = country || 'RUS';
  const [innerValue, setInnerValue] = useState(value);
  const InputComponent = mode === 'redesign' ? InputPlaceholder : Input;

  /**
   * Если в поле ввода вставляют полный номер телефона в котором первый символ отличается от символа
   * маски, (например 8 вместо +7 для России), то необходимо обрезать первые символы маски, чтобы сформировать правильный номер
   * Если вставляемое значение содержит меньше символов чем в полном формате номера, то оставляем без преобразований
   *
   * Например: вставляют 8 (900) 111-22-33, ожидаемый результат +7 (900) 111-22-33
   */
  const handleBeforePaste = useCallback(
    (pastedValue) => {
      const onlyNumbers: string = pastedValue.replace(/\D/g, '').toString();

      if (finalCountry === 'RUS' && onlyNumbers.length === 11) {
        return onlyNumbers.substring(1);
      }

      if (finalCountry === 'KAZ' && onlyNumbers[0] !== '7' && onlyNumbers.length === 11) {
        return `7${onlyNumbers.substring(1)}`;
      }

      if (finalCountry === 'BLR' && onlyNumbers.length === 12) {
        return onlyNumbers.substring(3);
      }

      if (finalCountry === 'UZB' && onlyNumbers.length === 12) {
        if (onlyNumbers.substring(0, 3) === '998') return onlyNumbers;

        return `998${onlyNumbers}`;
      }

      return onlyNumbers;
    },
    [finalCountry],
  );

  const handleInput = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value.replace(/\D/g, '').toString();

      /**
       * Если в поле вводят номер, начинающийся с 8, заменяем 8 на 7
       * Если вводится номер без 7 или 8 вначале - добавляем 7 к номеру
       */
      if (['RUS', 'KAZ'].includes(finalCountry)) {
        if (val === '' || val[0] === '7') return setInnerValue(val);

        if (val[0] === '8') return setInnerValue(`7${val.substring(1)}`);

        return setInnerValue(`7${val}`);
      }
      /**
       * Если вводят номер, начинающийся с 9 - заменяем 9 на
       * полный код страны (998).
       * Если вводится номер, начинающийся с любой другой цифры, то
       * добавляем 998 к номеру
       */
      if (finalCountry === 'UZB') {
        if (val.length === 1) {
          if (val === '9') return setInnerValue(`998${val.substring(1)}`);
          return setInnerValue(`998${val}`);
        }

        return setInnerValue(`998${val.substring(3)}`);
      }
    },
    [finalCountry],
  );

  return (
    <InputComponent
      {...restProps}
      mask={masks[finalCountry]}
      placeholder={placeholder ? placeholder : placeholders[finalCountry]}
      phonePlaceholder={mode === 'redesign' ? placeholders[finalCountry] : undefined}
      value={innerValue}
      autoComplete='off'
      onBeforePaste={handleBeforePaste}
      onInput={handleInput}
    />
  );
};

export default memo(InputPhone);
